/**
 * Helpers puros para renderizar y validar el formulario dinámico de
 * captura de encuestas. Ninguna función acá toca el DOM ni hace
 * peticiones; solo trabajan sobre `questions`/`answers` en memoria, lo
 * que las hace fáciles de razonar (y, a futuro, de testear).
 */

/** Bloques/secciones únicos de una encuesta, ordenados ascendentemente. */
export function getSurveyBlocks(questions) {
  return [...new Set(questions.map((q) => q.block))].sort((a, b) => a - b);
}

/** Índice `order` de pregunta → pregunta, para resolver `dependsOn`. */
export function buildQuestionsByOrder(questions) {
  const map = new Map();
  questions.forEach((q) => map.set(q.order, q));
  return map;
}

/**
 * Una pregunta es visible si no depende de otra, o si la pregunta de
 * la que depende ya fue respondida con el valor esperado (comparación
 * exacta contra el `value` de la opción elegida).
 */
export function isQuestionVisible(question, answers, questionsByOrder) {
  if (!question.dependsOn) return true;
  const target = questionsByOrder.get(question.dependsOn.questionOrder);
  if (!target) return true;
  return answers[target.id] === question.dependsOn.value;
}

/** `true` si una respuesta cuenta como "vacía" (incluye el shape de `image`). */
export function isAnswerEmpty(value) {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return !value.file;
  return false;
}

/** Errores de validación (`{ [questionId]: mensaje }`) de un conjunto de preguntas visibles. */
export function validateQuestions(questions, answers) {
  const errors = {};
  questions.forEach((q) => {
    if (q.required && isAnswerEmpty(answers[q.id])) {
      errors[q.id] = 'Este campo es obligatorio.';
    }
  });
  return errors;
}

/**
 * Traduce las respuestas en memoria (`{ [questionId]: value }`) al
 * arreglo `answers` que espera `POST /captures/sync`.
 *
 * Reglas:
 * - Solo se incluyen preguntas visibles (respeta `dependsOn`) y con
 *   respuesta (se omiten las opcionales sin responder).
 * - `single_choice` → resuelve `question_option_id` buscando la opción
 *   cuyo `value` coincide con la respuesta guardada.
 * - `short_text`/`long_text` → van en `text_value`.
 * - `image` → por ahora se omite: el endpoint de captura no tiene un
 *   campo para archivos; cuando exista un endpoint de subida de
 *   imágenes, este es el lugar para enlazarlo.
 *
 * @param {import('../services/surveyService').SurveyQuestion[]} questions
 * @param {Object} answers
 * @param {Map<number, Object>} questionsByOrder
 * @returns {Array<{ question_id: number, question_option_id: number|null, text_value: string|null, numeric_value: number|null }>}
 */
export function buildCaptureAnswers(questions, answers, questionsByOrder) {
  return questions
    .filter((q) => q.type !== 'image')
    .filter((q) => isQuestionVisible(q, answers, questionsByOrder))
    .filter((q) => !isAnswerEmpty(answers[q.id]))
    .map((q) => {
      const value = answers[q.id];

      if (q.type === 'single_choice') {
        const option = q.options.find((opt) => opt.value === value);
        return {
          question_id: Number(q.id),
          question_option_id: option ? Number(option.id) : null,
          text_value: null,
          numeric_value: null,
        };
      }

      return {
        question_id: Number(q.id),
        question_option_id: null,
        text_value: String(value),
        numeric_value: null,
      };
    });
}
